import { useRef, useState, useEffect, MutableRefObject } from "react";

export type HorizontalScrollOptions = {
  deps?: any[];
};

export type HorizontalScrollResult = {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollToEdge: (direction: "left" | "right") => void;
  scrollBy: (offset: number) => void;
};

export function useHorizontalScroll(options?: HorizontalScrollOptions): HorizontalScrollResult {
  const { deps = [] } = options || {};
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    // Catches reflow without a window resize (flex layout, late-loading images).
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const scrollToEdge = (direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;
    if (direction === "left") {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: "smooth" });
    }
  };

  const scrollBy = (offset: number) => {
    containerRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  return {
    containerRef,
    canScrollLeft,
    canScrollRight,
    scrollToEdge,
    scrollBy,
  };
}
